import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import SparkleIcon from '@cp/src/assets/icons/SparkleIcon';
import RecommendedModal from './components/RecommendedModal';
import { CaseRecommendationCopy, CaseRecommendationStatus } from './types';
import { openRecommendedModal, closeRecommendedModal } from './reducer';
import { RootState } from '@cp/src/store';
import {
  fetchCaseRecommendationCases,
  fetchCaseRecommendationMetadata
} from './caseRecommendationActions';
import CaseRecommendationIcon from '@cp/src/assets/icons/CaseRecommendationIcon';
import { DEFAULT_LIMIT_FOR_RECOMMENDED_CASES, DEFAULT_OFFSET_FOR_RECOMMENDED_CASES } from './utils';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import AnimatedBorderBox from '@cp/src/components/AnimatedBorderBox/AnimatedBorderBox';

const CaseRecommendation = () => {
  const dispatch = useDispatch();
  const { isModalOpen } = useSelector((state: RootState) => state.caseRecommendation);
  const recommendedCasesMetadata = useSelector(
    (state: RootState) => state.caseRecommendation.recommendedMetadata
  );
  const { caseList } = useSelector((state: RootState) => state.caseRecommendation.recommendedCases);

  const handleStartMyDay = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_DASHBOARD_BANNER_CLICK);
    dispatch(openRecommendedModal());

    if (!caseList?.length) {
      dispatch(
        fetchCaseRecommendationCases({
          limit: DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
          offset: DEFAULT_OFFSET_FOR_RECOMMENDED_CASES
        })
      );
    }
  };

  const handleCloseModal = () => {
    dispatch(closeRecommendedModal());
  };

  const headerText = useMemo(() => {
    if (recommendedCasesMetadata.visitedCases === 0) {
      return {
        text: CaseRecommendationCopy.INITIAL_TARGET_MESSAGE,
        startOrResumeText: CaseRecommendationCopy.START_COLLECTIONS
      };
    } else if (recommendedCasesMetadata.visitedCases < recommendedCasesMetadata.totalCases) {
      const remainingCases =
        recommendedCasesMetadata.totalCases - recommendedCasesMetadata.visitedCases;
      return {
        text: `${remainingCases} ${CaseRecommendationCopy.REMAINING_CASES_SUFFIX}`,
        startOrResumeText: CaseRecommendationCopy.RESUME_COLLECTIONS
      };
    }
    return {
      text: CaseRecommendationCopy.COMPLETION_MESSAGE,
      startOrResumeText: CaseRecommendationCopy.COLLECT_MORE_CASH
    };
  }, [recommendedCasesMetadata.visitedCases, recommendedCasesMetadata.totalCases]);

  useEffect(() => {
    dispatch(fetchCaseRecommendationMetadata());
  }, []);

  return (
    <>
      <div className="w-[96%] mx-8 my-6">
        <AnimatedBorderBox
          borderRadius={12}
          strokeWidth={1}
          animationDuration={1200}
          gradientStartColor="var(--blue-base)"
          gradientEndColor="var(--blue-border)"
          gradientId="recommendedBannerGradient"
          className="w-full"
        >
          <div className="flex flex-row justify-between items-center rounded-xl px-6 bg-gradient-to-r from-[var(--blue-bg)] to-[var(--bg-primary)]">
            <div className="flex flex-row items-center gap-4">
              <CaseRecommendationIcon />
              <div className="flex flex-col">
                <Typography variant="h2" color="var(--blue-dark)" className="font-extrabold">
                  {headerText.text}
                </Typography>
                <Typography variant="p3" color="var(--grayscale-2)">
                  Recommended <b>Case List</b> for you
                </Typography>
              </div>
            </div>
            <Button variant="primary" startAdornment={<SparkleIcon />} onClick={handleStartMyDay}>
              {headerText.startOrResumeText}
            </Button>
          </div>
        </AnimatedBorderBox>
      </div>
      <RecommendedModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};

export default CaseRecommendation;
